import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Eye,
  EyeOff,
  Image,
  LockKeyhole,
  Mail,
  MessageSquareText,
  PanelsTopLeft,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ErrorMessage } from '../components/Feedback';
import { getErrorMessage } from '../services/api';
export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  if (isAuthenticated) return <Navigate to="/" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
      console.log(getErrorMessage(err))
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[1.05fr_.95fr]">
      <section className="relative hidden overflow-hidden border-r border-line bg-gradient-to-br from-brand-50 via-white to-accent-50 p-12 lg:flex lg:flex-col lg:justify-between xl:p-16">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-brand-500/10 blur-3xl" />
        <div className="absolute -bottom-28 -left-16 h-80 w-80 rounded-full bg-accent-600/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-white shadow-sm">
            <img src="/logo.png" alt="PMS Logo" className="h-6 w-6" />
          </div>
          <div>
            <p className="text-lg font-bold tracking-tight text-ink">
              PMS <span className="text-brand-700">Admin</span>
            </p>
            <p className="text-xs text-ink-muted">Pixelmind Solutions</p>
          </div>
        </div>
        <div className="relative max-w-xl">
          <h1 className="text-4xl font-bold leading-[1.15] tracking-tight text-ink xl:text-5xl">
            Manage your website content in one place.
          </h1>
          <p className="mt-5 max-w-lg text-base leading-7 text-ink-muted">
            Update portfolio projects, publish banners, and respond to customer enquiries from one
            simple workspace.
          </p>
          <div className="mt-9 grid max-w-lg grid-cols-3 gap-3">
            {[
              [PanelsTopLeft, 'Portfolio'],
              [Image, 'Banners'],
              [MessageSquareText, 'Enquiries'],
            ].map(([Icon, label]) => (
              <div key={label} className="rounded-xl border border-line bg-white/80 p-4 shadow-soft">
                <Icon size={19} className="text-brand-700" />
                <p className="mt-3 text-sm font-semibold text-ink">{label}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-500">Secure administrator access · PMS Website</p>
      </section>
      <section className="flex min-h-screen items-center justify-center px-5 py-12 sm:px-10">
        <div className="w-full max-w-md">
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-accent-600 text-white">
              <Sparkles size={22} />
            </div>
            <div>
              <p className="text-lg font-extrabold">
                PMS <span className="text-brand-700">Admin</span>
              </p>
              <p className="text-xs text-ink-muted">Pixelmind Solutions</p>
            </div>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold tracking-tight text-ink">
              Sign in to your account
            </h2>
            <p className="mt-2 text-sm leading-6 text-ink-muted">
              Enter your administrator credentials to continue.
            </p>
          </div>
          <ErrorMessage>{error}</ErrorMessage>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label" htmlFor="email">
                Email address
              </label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  id="email"
                  className="input pl-11"
                  type="email"
                  autoComplete="username"
                  placeholder="admin@yourcompany.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <label className="label" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <LockKeyhole
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  id="password"
                  className="input pl-11 pr-12"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3">
              {loading ? 'Signing in...' : 'Sign in securely'}
              {!loading && <ArrowRight size={17} />}
            </button>
          </form>
          <div className="mt-8 flex items-start gap-3 rounded-lg border border-line bg-slate-50 p-4">
            <LockKeyhole size={17} className="mt-0.5 shrink-0 text-slate-400" />
            <p className="text-xs leading-5 text-ink-muted">
              This area is for authorised administrators only. Your session ends when you close the
              browser tab.
            </p>
          </div>
          <p className="mt-8 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} PMS Website. All rights reserved.
          </p>
        </div>
      </section>
    </div>
  );
}
